import { createSignal, onCleanup } from "solid-js";

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

export interface RelativeTimeLabels {
  justNow: string;
  past: (value: string) => string;
  future: (value: string) => string;
}

const defaultLabels: RelativeTimeLabels = {
  justNow: "just now",
  past: (v) => `${v} ago`,
  future: (v) => `in ${v}`,
};

export function formatRelative(
  input: Date | string | number | null | undefined,
  now = Date.now(),
  labels: RelativeTimeLabels = defaultLabels,
): string {
  if (input == null) return "";
  const d = input instanceof Date ? input : new Date(input);
  const ts = d.getTime();
  if (Number.isNaN(ts)) return "";

  const diff = Math.round((now - ts) / 1000);
  const abs = Math.abs(diff);
  const future = diff < 0;

  if (abs < 5) return labels.justNow;

  let unit: string;
  if (abs < MINUTE) unit = `${abs}s`;
  else if (abs < HOUR) unit = `${Math.floor(abs / MINUTE)}m`;
  else if (abs < DAY) unit = `${Math.floor(abs / HOUR)}h`;
  else if (abs < WEEK) unit = `${Math.floor(abs / DAY)}d`;
  else if (abs < MONTH) unit = `${Math.floor(abs / WEEK)}w`;
  else if (abs < YEAR) unit = `${Math.floor(abs / MONTH)}mo`;
  else unit = `${Math.floor(abs / YEAR)}y`;

  return future ? labels.future(unit) : labels.past(unit);
}

const subscribers = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function startTicker() {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    for (const fn of subscribers) fn();
  }, 30_000);
}

function stopTickerIfIdle() {
  if (subscribers.size === 0 && intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

export function useRelativeTimeTick(): () => number {
  const [tick, setTick] = createSignal(Date.now());
  const update = () => setTick(Date.now());
  subscribers.add(update);
  startTicker();
  onCleanup(() => {
    subscribers.delete(update);
    stopTickerIfIdle();
  });
  return tick;
}
